/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  Perm Address reducers
 */

import {
  falseReducer,
  nullReducer,
  payloadKeyReducer,
  payloadReducer,
  trueReducer,
} from '@zcode/reducers'
import type { PermanentAddress, RentHistoryDetailWithMeta } from '@zcode/state-type'
import { filterActions } from '@zumper/redux-ignore'
import reduceReducers from 'reduce-reducers'
import { type AnyAction, type CombinedState, combineReducers } from 'redux'
import { type ExtractActions, type Values, handleAction, handleActions } from 'redux-actions'
import invariant from 'tiny-invariant'
import type * as actions from '../actions'
import {
  DONE_FETCHING_RENT_HISTORY_DETAIL,
  ERROR_FETCHING_RENT_HISTORY_DETAIL,
  RECEIVE_ACTIVE_LISTINGS,
  RECEIVE_ADDRESS,
  RECEIVE_CITY,
  RECEIVE_DIL_ADDRESSES,
  RECEIVE_DIL_BUILDINGS,
  RECEIVE_HOMES_LISTINGS,
  RECEIVE_HOOD_PRICE_DATA,
  RECEIVE_INACTIVE_LISTINGS,
  RECEIVE_NEIGHBORHOOD,
  RECEIVE_PET_FRIENDLY_LISTINGS,
  RECEIVE_PET_POLICY,
  RECEIVE_PRICE_DATA,
  RECEIVE_RENT_HISTORY,
  RECEIVE_RENT_HISTORY_DETAIL,
  SET_HOMES_FETCHING,
  SET_PET_FRIENDLY_FETCHING,
  START_FETCHING_RENT_HISTORY_DETAIL,
} from '../constants'
import { allowedList } from './allowedList'
import { messageReducer } from './message'

type Actions = ExtractActions<Values<typeof actions>>

const isActionOfType = <Type extends Actions['type']>(
  action: AnyAction,
  type: Type
): action is Actions & { type: Type } => action.type === type

const rootReducer = combineReducers<PermanentAddress, Actions>({
  message: messageReducer,
  activeListings: handleAction<
    PermanentAddress['activeListings'],
    Actions,
    typeof RECEIVE_ACTIVE_LISTINGS
  >(RECEIVE_ACTIVE_LISTINGS, payloadReducer, null),
  address: handleAction<PermanentAddress['address'], Actions, typeof RECEIVE_ADDRESS>(
    RECEIVE_ADDRESS,
    payloadReducer,
    null
  ),
  city: handleAction<PermanentAddress['city'], Actions, typeof RECEIVE_CITY>(
    RECEIVE_CITY,
    payloadReducer,
    null
  ),
  forbiddenListingIds: handleAction<
    PermanentAddress['forbiddenListingIds'],
    Actions,
    typeof RECEIVE_INACTIVE_LISTINGS
  >(
    RECEIVE_INACTIVE_LISTINGS,
    (state, { payload }) => {
      if (!payload?.length) {
        return null
      }
      const forbiddenListingIds = []
      for (const { isInactiveListhub, listingId } of payload) {
        if (isInactiveListhub) {
          forbiddenListingIds.push(listingId)
        }
      }
      return forbiddenListingIds.length ? forbiddenListingIds : null
    },
    null
  ),
  hoodPriceData: handleAction<
    PermanentAddress['hoodPriceData'],
    Actions,
    typeof RECEIVE_HOOD_PRICE_DATA
  >(RECEIVE_HOOD_PRICE_DATA, payloadReducer, null),
  inactiveListings: handleAction<
    PermanentAddress['inactiveListings'],
    Actions,
    typeof RECEIVE_INACTIVE_LISTINGS
  >(
    RECEIVE_INACTIVE_LISTINGS,
    // Data from inactive listhub listings must not be displayed anywhere
    (state, { payload }) => payload?.filter(({ isInactiveListhub }) => !isInactiveListhub) ?? null,
    null
  ),
  petFriendlyListings: combineReducers({
    listables: handleAction<
      PermanentAddress['petFriendlyListings']['listables'],
      Actions,
      typeof RECEIVE_PET_FRIENDLY_LISTINGS
    >(RECEIVE_PET_FRIENDLY_LISTINGS, payloadReducer, null),
    fetching: handleAction<
      PermanentAddress['petFriendlyListings']['fetching'],
      Actions,
      typeof SET_PET_FRIENDLY_FETCHING
    >(SET_PET_FRIENDLY_FETCHING, payloadReducer, false),
  }),
  homesListings: combineReducers({
    listables: handleAction<
      PermanentAddress['homesListings']['listables'],
      Actions,
      typeof RECEIVE_HOMES_LISTINGS
    >(RECEIVE_HOMES_LISTINGS, payloadReducer, null),
    fetching: handleAction<
      PermanentAddress['homesListings']['fetching'],
      Actions,
      typeof SET_HOMES_FETCHING
    >(SET_HOMES_FETCHING, payloadReducer, false),
  }),
  neighborhood: handleAction<
    PermanentAddress['neighborhood'],
    Actions,
    typeof RECEIVE_NEIGHBORHOOD
  >(RECEIVE_NEIGHBORHOOD, payloadReducer, null),
  priceData: handleAction<PermanentAddress['priceData'], Actions, typeof RECEIVE_PRICE_DATA>(
    RECEIVE_PRICE_DATA,
    payloadReducer,
    null
  ),
  rentHistoryDetail: reduceReducers(
    combineReducers({
      data: handleActions<RentHistoryDetailWithMeta['data'], Actions>(
        {
          [RECEIVE_RENT_HISTORY_DETAIL]: payloadKeyReducer('data'),
        },
        null
      ),
      meta: combineReducers({
        currentPaUrl: handleActions<RentHistoryDetailWithMeta['meta']['currentPaUrl'], Actions>(
          {
            [ERROR_FETCHING_RENT_HISTORY_DETAIL]: payloadKeyReducer('paUrl'),
            [RECEIVE_RENT_HISTORY_DETAIL]: payloadKeyReducer('paUrl'),
            [START_FETCHING_RENT_HISTORY_DETAIL]: nullReducer,
          },
          null
        ),
        error: handleActions(
          {
            [RECEIVE_RENT_HISTORY_DETAIL]: falseReducer,
            [ERROR_FETCHING_RENT_HISTORY_DETAIL]: trueReducer,
            [START_FETCHING_RENT_HISTORY_DETAIL]: falseReducer,
          },
          false
        ),
        fetchingPaUrl: handleActions<RentHistoryDetailWithMeta['meta']['fetchingPaUrl'], Actions>(
          {
            [START_FETCHING_RENT_HISTORY_DETAIL]: payloadKeyReducer('paUrl'),
            [DONE_FETCHING_RENT_HISTORY_DETAIL]: nullReducer,
          },
          null
        ),
      }),
    }),
    (
      state: CombinedState<RentHistoryDetailWithMeta> | undefined,
      action: Actions | AnyAction
    ): CombinedState<RentHistoryDetailWithMeta> => {
      invariant(state)
      if (
        state.meta.currentPaUrl &&
        isActionOfType(action, RECEIVE_ADDRESS) &&
        action.payload.paUrl !== state.meta.currentPaUrl
      ) {
        return {
          data: null,
          meta: {
            ...state.meta,
            currentPaUrl: null,
            error: false,
          },
        }
      }
      return state
    }
  ),
  rentHistorySummary: handleAction<
    PermanentAddress['rentHistorySummary'],
    Actions,
    typeof RECEIVE_RENT_HISTORY
  >(RECEIVE_RENT_HISTORY, payloadReducer, null),
  petPolicy: handleAction<PermanentAddress['petPolicy'], Actions, typeof RECEIVE_PET_POLICY>(
    RECEIVE_PET_POLICY,
    payloadReducer,
    null /* schema had default of ImList() */
  ),
  dilAddresses: handleAction<
    PermanentAddress['dilAddresses'],
    Actions,
    typeof RECEIVE_DIL_ADDRESSES
  >(RECEIVE_DIL_ADDRESSES, payloadReducer, null /* schema had default of ImList() */),
  dilBuildings: handleAction<
    PermanentAddress['dilBuildings'],
    Actions,
    typeof RECEIVE_DIL_BUILDINGS
  >(RECEIVE_DIL_BUILDINGS, payloadReducer, null /* schema had default of ImList() */),
})

export const permanentAddressReducer = filterActions(rootReducer, allowedList)
